import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ArrowRight, Users } from "lucide-react";

import { api } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import {
  Badge,
  Card,
  EmptyState,
  LimitBar,
  PageHeader,
  Skeleton,
  TableSkeleton,
  cx,
} from "../components/ui";
import { PILLARS } from "../app/navigation";
import { countLabel, formatBytes, formatNumber, plural } from "../lib/format";
import type { Contact } from "../api/types";

/**
 * The overview. Every number on this page comes from a real endpoint --
 * `/account/{id}/usage` for limits, `/contacts` for the recent list. Nothing is
 * invented, because a dashboard that shows plausible fiction is worse than one
 * that admits it is early.
 */
export function DashboardPage() {
  const { user } = useAuth();
  const account = user?.account;
  const label = account?.primary_object_label ?? "Contacts";
  // `enabled` below guarantees this is present when the query runs.
  const accountId = account?.id ?? "";

  const usage = useQuery({
    queryKey: ["usage", account?.id],
    queryFn: ({ signal }) => api.usage(accountId, signal),
    enabled: accountId !== undefined,
  });

  const recent = useQuery({
    queryKey: ["contacts", "recent"],
    queryFn: ({ signal }) => api.contacts({ page_size: 5 }, signal),
  });

  const contactsUsed = usage.data?.usage.contacts.current ?? 0;
  const loginsUsed = usage.data?.usage.users.current ?? 0;
  const modules = usage.data?.modules ?? [];
  // "5 modules enabled" reads like a cap. When the whole product is included,
  // say so; only fall back to a count if a module is genuinely missing.
  const allModulesEnabled = ["customers", "billing", "people"].every((code) =>
    modules.includes(code),
  );

  return (
    <>
      <PageHeader
        title={`Good to see you, ${user?.first_name || user?.full_name || "there"}`}
        subtitle={`${account?.name ?? ""} · ${label} overview`}
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile
          label={label}
          value={usage.isPending ? null : formatNumber(contactsUsed)}
          to="/customers/contacts"
          hint="Across all lifecycle stages"
        />
        <StatTile
          label="Logins"
          value={usage.isPending ? null : formatNumber(loginsUsed)}
          to="/settings/plan"
          hint="People who can sign in"
        />
        <StatTile
          label="Plan"
          value={usage.data?.plan?.name ?? (usage.isPending ? null : "—")}
          to="/settings/plan"
          hint={
            usage.isPending
              ? "—"
              : allModulesEnabled
                ? "All modules enabled"
                : countLabel(modules.length, "module") + " enabled"
          }
        />
        <StatTile
          label="Storage"
          value={
            usage.isPending
              ? null
              : formatBytes(usage.data?.usage.storage_bytes.current ?? 0)
          }
          to="/settings/plan"
          hint="Documents and media"
        />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-900">Recently added</h2>
            <Link
              to="/customers/contacts"
              className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
            >
              View all <ArrowRight size={14} />
            </Link>
          </div>

          {recent.isPending ? (
            <TableSkeleton rows={4} />
          ) : recent.isError ? (
            <EmptyState title="Could not load contacts" body="Check the API is running." />
          ) : recent.data && recent.data.results.length > 0 ? (
            <ul className="divide-y divide-slate-100">
              {recent.data.results.map((contact) => (
                <li key={contact.id}>
                  <Link
                    to={`/customers/contacts/${contact.id}`}
                    className="flex items-center gap-3 py-2.5 hover:bg-slate-50"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-slate-900">
                        {contact.display_name}
                      </p>
                      <p className="truncate text-xs text-slate-500">
                        {[contact.email, contact.phone].filter(Boolean).join(" · ") || "—"}
                      </p>
                    </div>
                    <StageTag stage={contact.lifecycle_stage} />
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState
              icon={<Users size={28} />}
              title={`No ${plural(label)} yet`}
              body="Add your first record and it will appear here."
              action={
                <Link
                  to="/customers/contacts"
                  className="text-sm font-medium text-slate-900 underline"
                >
                  Go to {plural(label)}
                </Link>
              }
            />
          )}
        </Card>

        <div className="space-y-4">
          <Card>
            <h2 className="mb-4 text-sm font-semibold text-slate-900">Plan usage</h2>
            {usage.isPending ? (
              <div className="space-y-4">
                <Skeleton className="h-8" />
                <Skeleton className="h-8" />
                <Skeleton className="h-8" />
              </div>
            ) : usage.data ? (
              <div className="space-y-4">
                <LimitBar
                  label={label}
                  value={usage.data.usage.contacts.current}
                  limit={usage.data.usage.contacts.limit}
                  format={formatNumber}
                />
                <LimitBar
                  label="Logins"
                  value={usage.data.usage.users.current}
                  limit={usage.data.usage.users.limit}
                  format={formatNumber}
                />
                <LimitBar
                  label="Employees"
                  value={usage.data.usage.employees.current}
                  limit={usage.data.usage.employees.limit}
                  format={formatNumber}
                />
                <LimitBar
                  label="Storage"
                  value={usage.data.usage.storage_bytes.current}
                  limit={usage.data.usage.storage_bytes.limit}
                  format={formatBytes}
                />
                <Link
                  to="/settings/plan"
                  className="inline-block pt-1 text-sm text-slate-600 underline hover:text-slate-900"
                >
                  Plan details
                </Link>
              </div>
            ) : null}
          </Card>

          <Card>
            <h2 className="mb-1 text-sm font-semibold text-slate-900">Your product</h2>
            <p className="mb-3 text-xs text-slate-500">
              Three parts, one record for the people in them.
            </p>
            <ul className="space-y-2">
              {PILLARS.filter((p) => ["customers", "people", "billing"].includes(p.id)).map(
                (pillar) => {
                  const enabled = pillar.moduleCode
                    ? usage.data?.modules.includes(pillar.moduleCode)
                    : null;
                  return (
                    <li key={pillar.id} className="flex items-start gap-2">
                      {enabled === null ? (
                        <span className="mt-1 size-1.5 shrink-0 rounded-full bg-slate-300" />
                      ) : (
                        <span
                          className={cx(
                            "mt-1 size-1.5 shrink-0 rounded-full",
                            enabled ? "bg-emerald-500" : "bg-slate-300",
                          )}
                        />
                      )}
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-800">{pillar.label}</p>
                        <p className="text-xs text-slate-500">{pillar.blurb}</p>
                      </div>
                      {enabled === false ? (
                        <Badge tone="slate">Locked</Badge>
                      ) : null}
                    </li>
                  );
                },
              )}
            </ul>
          </Card>
        </div>
      </div>
    </>
  );
}

function StatTile({
  label,
  value,
  hint,
  to,
}: {
  label: string;
  value: string | null;
  hint: string;
  to: string;
}) {
  return (
    <Link to={to} className="group">
      <Card className="transition group-hover:border-slate-300">
        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
        {value === null ? (
          <Skeleton className="mt-2 h-7 w-16" />
        ) : (
          <p className="mt-1 text-2xl font-semibold tracking-tight text-slate-900">{value}</p>
        )}
        <p className="mt-1 truncate text-xs text-slate-500">{hint}</p>
      </Card>
    </Link>
  );
}

function StageTag({ stage }: { stage: Contact["lifecycle_stage"] }) {
  return (
    <span className="shrink-0 text-xs capitalize text-slate-500">{stage ?? "—"}</span>
  );
}